/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IActionContext } from '@msdyn365-commerce/core';
import { CartLine } from '@msdyn365-commerce/retail-proxy';

import { ArrayExtensions } from './extensions/array-extensions';
import { IProductInventoryInformation } from './product-inventory-information';

/**
 * Cart line inventory validation result interface.
 */
export interface ICartLineInventoryValidationResult {

    /**
     * Define whether the cart lines inventory is valid across multiple cart lines.
     */
    readonly isValid: boolean;

    /**
     * Defines the list of product id with invalid inventory.
     */
    readonly productIdWithErrors: number [];
}

/**
 * Validates inventory across cart lines.
 * @param cartLines - The cart lines.
 * @param productAvailabilities - The product availability collection.
 * @param actionContext - The action context.
 * @returns True if all product quantity are available, false otherwise.
 */
export function validateCartLinesInventory(
    cartLines: CartLine[], productAvailabilities: IProductInventoryInformation[], actionContext: IActionContext): ICartLineInventoryValidationResult {
    const cartLineMap = new Map<number, number>();
    const defaultCartLineQuantity = 1;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Disabling rule validation for this line.
    if (!actionContext.requestContext.app.config.enableStockCheck) {
        return {
            isValid: true,
            productIdWithErrors: []
        };
    }
    const productIdWithErrors = [];
    let isValid = true;

    if (ArrayExtensions.hasElements(cartLines) && ArrayExtensions.hasElements(productAvailabilities)) {

        // Consolidate products in different cart lines into single entry with the total cart quantity
        for (const cartLine of cartLines) {
            // Skip validation if is an invoice inline.
            if (cartLine.ProductId && !cartLine.IsInvoiceLine) {
                if (!cartLineMap.has(cartLine.ProductId)) {
                    cartLineMap.set(cartLine.ProductId, cartLine.Quantity ?? defaultCartLineQuantity);
                } else {
                    const cartLineTotal = cartLineMap.get(cartLine.ProductId) ?? 0;
                    cartLineMap.delete(cartLine.ProductId);
                    cartLineMap.set(cartLine.ProductId, cartLineTotal + (cartLine.Quantity ?? defaultCartLineQuantity));
                }
            }
        }

        // Hashing product availability object by product ID.
        const productAvailabilityMap = new Map<number, IProductInventoryInformation>();
        for (const productAvailability of productAvailabilities) {

            if (productAvailability.ProductAvailableQuantity.ProductId &&
                !productAvailabilityMap.has(productAvailability.ProductAvailableQuantity.ProductId)) {
                productAvailabilityMap.set(productAvailability.ProductAvailableQuantity.ProductId, productAvailability);
            }
        }

        // Compare total quantity with the available quantity from the inventory API, also validate that the product is available.
        for (const productId of Array.from(cartLineMap.keys())) {
            const cartLineQty = cartLineMap.get(productId) ?? defaultCartLineQuantity;
            const productAvailability = productAvailabilityMap.get(productId);
            const availableQuantity = productAvailability?.ProductAvailableQuantity.AvailableQuantity ?? undefined;
            if (!productAvailability?.IsProductAvailable || (availableQuantity && cartLineQty > availableQuantity)) {
                productIdWithErrors.push(productId);
                isValid = false;
            }
        }
    }
    return {
        isValid,
        productIdWithErrors
    };
}
